import { z } from "zod/v4"
import {
  LiveCommentIdSchema,
  LiveIdSchema,
  SelectionIdSchema,
  SelectionParticipantIdSchema,
  SpaceKeySchema,
  SpaceMessageIdSchema,
  StoryIdSchema,
  UserIdSchema,
  ViolationReportId,
  ViolationReportIdSchema,
  VoiceIdSchema,
  VoiceReplyIdSchema,
} from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { validateUserFreeInputText } from "../user-free-input-text-constraint"
import { assertNever } from "../lib/assert-never"

export const ViolationReportTargetKindSchema = z.enum([
  "user",
  "user-profile",
  "space",
  "story",
  "live-comment",
  "space-message",
  "selection-participant",
  "live",
  "voice",
  "voice-reply",
])

export type ViolationReportTargetKind = z.infer<typeof ViolationReportTargetKindSchema>

export const ViolationReportReasonSchema = z.enum([
  "sexual",
  "violence",
  "grotesque",
  "offensive",
  "discriminatory",
  "cruel",
  "illegal",
  "copyright",
  "cheating",
  "other",
])

export type ViolationReportReason = z.infer<typeof ViolationReportReasonSchema>

export const ViolationReportStatusSchema = z.enum([
  "pending",
  "in-progress",
  "resolved",
  "dismissed",
])

export type ViolationReportStatus = z.infer<typeof ViolationReportStatusSchema>

export class ViolationReports {
  static readonly DETAIL_MAX_LENGTH = 1000

  static readonly targetKinds = [
    { value: "user", description: "ユーザー全般（使用ルック等）" },
    { value: "user-profile", description: "プロフィール（名前、エイリアス、自己紹介文）" },
    { value: "space", description: "スペース（タイトル等）" },
    { value: "story", description: "ストーリーズ" },
    { value: "live-comment", description: "ライブコメント" },
    { value: "space-message", description: "スペースメッセージ" },
    { value: "selection-participant", description: "募集参加（画像・説明文）" },
    { value: "live", description: "ライブでの配信者の語る内容" },
    { value: "voice", description: "ボイス投稿" },
    { value: "voice-reply", description: "ボイスへの返信" },
  ] as const satisfies ReadonlyArray<{ value: ViolationReportTargetKind; description: string }>

  static readonly reasons = [
    { value: "sexual", description: "性的な表現が含まれている" },
    { value: "violence", description: "暴力的な表現が含まれている" },
    { value: "grotesque", description: "グロテスクな表現が含まれている" },
    { value: "offensive", description: "不快な表現が含まれている" },
    { value: "discriminatory", description: "差別的な表現が含まれている" },
    { value: "cruel", description: "残酷な表現が含まれている" },
    { value: "illegal", description: "法令に違反する内容が含まれている (権利侵害以外)" },
    { value: "copyright", description: "権利侵害" },
    { value: "cheating", description: "チート・不正利用" },
    { value: "other", description: "その他" },
  ] as const satisfies ReadonlyArray<{ value: ViolationReportReason; description: string }>

  static readonly statuses = [
    { value: "pending", description: "未確認" },
    { value: "in-progress", description: "対応中" },
    { value: "resolved", description: "対応完了" },
    { value: "dismissed", description: "対応しない" },
  ] as const satisfies ReadonlyArray<{ value: ViolationReportStatus; description: string }>
}

// Firestore 保存用スキーマ (snake_case)
export const ViolationReportTargetUserSchema = z.object({
  kind: z.literal("user"),
  user_id: UserIdSchema,
}).describe("ユーザー全般（使用ルック等）に対する通報")

export const ViolationReportTargetUserProfileSchema = z.object({
  kind: z.literal("user-profile"),
  user_id: UserIdSchema,
}).describe("ユーザープロフィール（名前、エイリアス、自己紹介文）に対する通報")

export const ViolationReportTargetSpaceSchema = z.object({
  kind: z.literal("space"),
  space_key: SpaceKeySchema,
}).describe("スペース（タイトル等）に対する通報")

export const ViolationReportTargetStorySchema = z.object({
  kind: z.literal("story"),
  user_id: UserIdSchema,
  story_id: StoryIdSchema,
}).describe("ストーリーズに対する通報")

export const ViolationReportTargetLiveCommentSchema = z.object({
  kind: z.literal("live-comment"),
  space_key: SpaceKeySchema,
  live_id: LiveIdSchema,
  comment_id: LiveCommentIdSchema,
}).describe("ライブコメントに対する通報")

export const ViolationReportTargetSpaceMessageSchema = z.object({
  kind: z.literal("space-message"),
  space_key: SpaceKeySchema,
  message_id: SpaceMessageIdSchema,
}).describe("スペースメッセージに対する通報")

export const ViolationReportTargetSelectionParticipantSchema = z.object({
  kind: z.literal("selection-participant"),
  space_key: SpaceKeySchema,
  live_id: LiveIdSchema,
  selection_id: SelectionIdSchema,
  participant_id: SelectionParticipantIdSchema,
}).describe("募集参加（画像・説明文）に対する通報")

export const ViolationReportTargetLiveSchema = z.object({
  kind: z.literal("live"),
  space_key: SpaceKeySchema,
  live_id: LiveIdSchema,
}).describe("ライブでの配信者の語る内容に対する通報")

export const ViolationReportTargetVoiceSchema = z.object({
  kind: z.literal("voice"),
  user_id: UserIdSchema,
  voice_id: VoiceIdSchema,
}).describe("ボイス投稿に対する通報")

export const ViolationReportTargetVoiceReplySchema = z.object({
  kind: z.literal("voice-reply"),
  user_id: UserIdSchema,
  voice_id: VoiceIdSchema,
  voice_reply_id: VoiceReplyIdSchema,
}).describe("ボイスへの返信に対する通報")

export const ViolationReportTargetSchema = z.discriminatedUnion("kind", [
  ViolationReportTargetUserSchema,
  ViolationReportTargetUserProfileSchema,
  ViolationReportTargetSpaceSchema,
  ViolationReportTargetStorySchema,
  ViolationReportTargetLiveCommentSchema,
  ViolationReportTargetSpaceMessageSchema,
  ViolationReportTargetSelectionParticipantSchema,
  ViolationReportTargetLiveSchema,
  ViolationReportTargetVoiceSchema,
  ViolationReportTargetVoiceReplySchema,
]).describe("通報対象のコンテンツ。kindによって必須フィールドが異なる。(Firestore保存用)")

export type ViolationReportTarget = z.infer<typeof ViolationReportTargetSchema>

// API リクエスト用スキーマ (camelCase) - OpenAPI 仕様に準拠
// Note: "Request" suffix を使用することで lint ルールが camelCase を許可する
export const ViolationReportTargetUserRequest = z.object({
  kind: z.literal("user"),
  userId: UserIdSchema,
}).describe("ユーザー全般（使用ルック等）に対する通報")

export const ViolationReportTargetUserProfileRequest = z.object({
  kind: z.literal("user-profile"),
  userId: UserIdSchema,
}).describe("ユーザープロフィール（名前、エイリアス、自己紹介文）に対する通報")

export const ViolationReportTargetSpaceRequest = z.object({
  kind: z.literal("space"),
  spaceKey: SpaceKeySchema,
}).describe("スペース（タイトル等）に対する通報")

export const ViolationReportTargetStoryRequest = z.object({
  kind: z.literal("story"),
  userId: UserIdSchema,
  storyId: StoryIdSchema,
}).describe("ストーリーズに対する通報")

export const ViolationReportTargetLiveCommentRequest = z.object({
  kind: z.literal("live-comment"),
  spaceKey: SpaceKeySchema,
  liveId: LiveIdSchema,
  commentId: LiveCommentIdSchema,
}).describe("ライブコメントに対する通報")

export const ViolationReportTargetSpaceMessageRequest = z.object({
  kind: z.literal("space-message"),
  spaceKey: SpaceKeySchema,
  messageId: SpaceMessageIdSchema,
}).describe("スペースメッセージに対する通報")

export const ViolationReportTargetSelectionParticipantRequest = z.object({
  kind: z.literal("selection-participant"),
  spaceKey: SpaceKeySchema,
  liveId: LiveIdSchema,
  selectionId: SelectionIdSchema,
  participantId: SelectionParticipantIdSchema,
}).describe("募集参加（画像・説明文）に対する通報")

export const ViolationReportTargetLiveRequest = z.object({
  kind: z.literal("live"),
  spaceKey: SpaceKeySchema,
  liveId: LiveIdSchema,
}).describe("ライブでの配信者の語る内容に対する通報")

export const ViolationReportTargetVoiceRequest = z.object({
  kind: z.literal("voice"),
  userId: UserIdSchema,
  voiceId: VoiceIdSchema,
}).describe("ボイス投稿に対する通報")

export const ViolationReportTargetVoiceReplyRequest = z.object({
  kind: z.literal("voice-reply"),
  userId: UserIdSchema,
  voiceId: VoiceIdSchema,
  voiceReplyId: VoiceReplyIdSchema,
}).describe("ボイスへの返信に対する通報")

export const ViolationReportTargetRequest = z.discriminatedUnion("kind", [
  ViolationReportTargetUserRequest,
  ViolationReportTargetUserProfileRequest,
  ViolationReportTargetSpaceRequest,
  ViolationReportTargetStoryRequest,
  ViolationReportTargetLiveCommentRequest,
  ViolationReportTargetSpaceMessageRequest,
  ViolationReportTargetSelectionParticipantRequest,
  ViolationReportTargetLiveRequest,
  ViolationReportTargetVoiceRequest,
  ViolationReportTargetVoiceReplyRequest,
]).describe("通報対象のコンテンツ。kindによって必須フィールドが異なる。(API用)")

export type ViolationReportTargetRequest = z.infer<typeof ViolationReportTargetRequest>

// API target (camelCase) を Firestore target (snake_case) に変換
export function convertApiTargetToFirestoreTarget(apiTarget: ViolationReportTargetRequest): ViolationReportTarget {
  switch (apiTarget.kind) {
    case "user":
      return { kind: "user", user_id: apiTarget.userId }
    case "user-profile":
      return { kind: "user-profile", user_id: apiTarget.userId }
    case "space":
      return { kind: "space", space_key: apiTarget.spaceKey }
    case "story":
      return { kind: "story", user_id: apiTarget.userId, story_id: apiTarget.storyId }
    case "live-comment":
      return { kind: "live-comment", space_key: apiTarget.spaceKey, live_id: apiTarget.liveId, comment_id: apiTarget.commentId }
    case "space-message":
      return { kind: "space-message", space_key: apiTarget.spaceKey, message_id: apiTarget.messageId }
    case "selection-participant":
      return { kind: "selection-participant", space_key: apiTarget.spaceKey, live_id: apiTarget.liveId, selection_id: apiTarget.selectionId, participant_id: apiTarget.participantId }
    case "live":
      return { kind: "live", space_key: apiTarget.spaceKey, live_id: apiTarget.liveId }
    case "voice":
      return { kind: "voice", user_id: apiTarget.userId, voice_id: apiTarget.voiceId }
    case "voice-reply":
      return { kind: "voice-reply", user_id: apiTarget.userId, voice_id: apiTarget.voiceId, voice_reply_id: apiTarget.voiceReplyId }
    default:
      return assertNever(apiTarget)
  }
}

export const ViolationReportDocumentMeta = z.object({
  reporter_user_id: UserIdSchema.describe("通報者のユーザーID"),
  user_id: UserIdSchema.describe("通報対象のユーザーID"),
  target: ViolationReportTargetSchema.describe("通報対象"),
  reason: ViolationReportReasonSchema.describe("通報の理由"),
  detail: z.string().max(ViolationReports.DETAIL_MAX_LENGTH).optional().describe("通報内容の詳細"),
  status: ViolationReportStatusSchema.describe("通報に対する対応状況"),
  memo: z.string().optional().describe("運営のメモ"),
})

export type ViolationReportDocumentMeta = z.infer<typeof ViolationReportDocumentMeta>

export const ViolationReportDocumentBody = z.intersection(
  ViolationReportDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
).describe("ユーザーからの他のユーザーに対しての通報")

export type ViolationReportDocumentBody = z.infer<typeof ViolationReportDocumentBody>

export const ViolationReportDocument = z.intersection(
  ViolationReportDocumentBody,
  z.object({
    id: ViolationReportIdSchema,
  })
)

export type ViolationReportDocument = z.infer<typeof ViolationReportDocument>

export class ViolationReportDocuments {

  static toDocumentData(violationReport: ViolationReportDocument): object {
    return {
      ...violationReport,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ViolationReportDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: ViolationReportId(doc.id),
      reporter_user_id: data["reporter_user_id"],
      user_id: data["user_id"],
      target: data["target"],
      reason: data["reason"],
      detail: data["detail"],
      status: data["status"],
      memo: data["memo"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateViolationReportRequest = z.object({
  target: ViolationReportTargetRequest,
  reason: ViolationReportReasonSchema,
  detail: validateUserFreeInputText({minLength: 0, maxLength: ViolationReports.DETAIL_MAX_LENGTH}).optional(),
})

export type CreateViolationReportRequest = z.infer<typeof CreateViolationReportRequest>

export const UpdateViolationReportRequest = z.object({
  status: ViolationReportStatusSchema.optional(),
  memo: z.string().optional(),
})

export type UpdateViolationReportRequest = z.infer<typeof UpdateViolationReportRequest>
